import * as R from "ramda";
import React from "react";
import * as Autosuggest from "react-autosuggest";
import { FaPlus, FaTrash, FaUser, FaUserFriends } from "react-icons/fa";
import styled from "styled-components";

import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IDisplayInfoResponse, IResolveResponse } from "../../../api/ResolveApi/resolve-api.types";
import ActionConfirmationButton from "../../../components/pages/CourseAuthor/ActionConfirmation";
import AutoComplete from "../../../components/pages/CourseAuthor/Autocomplete";
import { UserLink, UserName } from "../../../components/pages/Users";
import { dependencies } from "../../../dependencies";
import { IUsersUIData, toTuple } from "../../../parsers/resolve";
import { AddButton, ButtonRow, CustomSearchInput, FieldSet } from "../../Common/Components/FormComponents";
import { autoCompleteCustomStyles, Section } from "../../Common/Styles/containers";
import { IClass } from "../../types/common";

const UserRow = styled.div`
  display: flex;
`;

const StyledMemberLink = styled(UserLink)`
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Legend = styled.legend`
  color: ${({ theme }) => theme.colors.buttonDarkBlue};
`;

const CustomUser = styled(FaUser)`
  min-width: 1em;
`;
const CustomGroup = styled(FaUserFriends)`
  min-width: 1em;
`;
interface IClassUsersProps {
  allUsers: IUsersUIData[];
  userType: UserType;
  class: IClass;
  classId: string;
  currentUserId: string;
  currentOrgId: string;
  add: (userId: string) => void;
  remove: (userName: string) => void;
}
export enum UserType {
  Member = "Members",
  Manager = "Managers",
}
const getEligibleUsersIds = (
  currentUserId: string,
  currentOrgId: string,
  q?: string
): Promise<AxiosInterceptedResult<string[]>> => {
  return dependencies.api.autocomplete.getEligibleEditors(currentUserId, currentOrgId)(q);
};

const onSuggestionsFetchRequested = (value: string) => async (currentUserId: string, currentOrgId: string) => {
  const [error, resp] = await getEligibleUsersIds(currentUserId, currentOrgId, value);
  if (R.isNil(error) && !R.isNil(resp)) {
    const unformattedUsersIds = resp.data;
    const userIds = unformattedUsersIds.map(toTuple);
    const [errorNames, respNames] = await dependencies.api.resolve.displayInfo.post(currentUserId, userIds);
    if (R.isNil(errorNames) && !R.isNil(respNames)) {
      const { data } = respNames;
      const usersInfo = data.map((entry: IResolveResponse<IDisplayInfoResponse>) => entry.value);
      const users = R.zip(unformattedUsersIds, usersInfo) as UserData[];
      return users;
    }
  }
  return [];
};
const isGroupOrUser = (id: string) => {
  const prefix = R.slice(0, 3, id);
  return prefix === "grp";
};
const renderUserRow = (user: IUsersUIData, deleteRow: (id: string) => void): React.ReactNode => {
  const { id, displayName } = user;

  const performDelete = () => deleteRow(id);
  return (
    <UserRow key={id}>
      <StyledMemberLink to={``}>
        {isGroupOrUser(id) ? <CustomGroup /> : <CustomUser />}
        <UserName>{displayName}</UserName>
      </StyledMemberLink>
      <ActionConfirmationButton action="Delete" actionFn={performDelete} ActionIcon={FaTrash} />
    </UserRow>
  );
};

export type UserData = [string, IDisplayInfoResponse];
const UsersAndGroupsManagement: React.FC<IClassUsersProps> = props => {
  const autosuggestRef = React.useRef<AutoComplete<UserData>>(null);
  const [selected, setSelected] = React.useState<UserData | null>(null);
  const [eligibleUsers, setEligibleUsers] = React.useState<UserData[]>([]);

  const { add, remove } = props;
  const addNewUser = () => {
    selected && add(selected[0]);
    setSelected(null);
    autosuggestRef.current && autosuggestRef.current.clearInput();
  };
  const onSuggestionSetSelected = async ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => {
    const usersResolved = await onSuggestionsFetchRequested(value)(props.currentUserId, props.currentOrgId);
    return setEligibleUsers(usersResolved);
  };

  const selectNewUser = (suggestion: [string, IDisplayInfoResponse]) => setSelected(suggestion);

  return (
    <Section>
      <h2>{props.userType}</h2>
      <FormContainer>
        <FieldSet>
          <Legend>Enter a User or Group Name</Legend>
          <AutoComplete
            ref={autosuggestRef}
            customStyles={autoCompleteCustomStyles}
            fetchSuggestions={onSuggestionSetSelected}
            htmlName={"choose-user"}
            items={eligibleUsers}
            itemValue={R.pathOr("", ["1", "displayName"])}
            placeholderText="Type here to look for User or Group"
            renderInputComponent={CustomSearchInput}
            setSelected={selectNewUser}
          />
        </FieldSet>
        <ButtonRow width={15} margin={1}>
          <AddButton disabled={selected == null} onClick={addNewUser}>
            <FaPlus /> Add {props.userType.slice(0, -1)}
          </AddButton>
        </ButtonRow>
      </FormContainer>
      {props.allUsers.map(user => renderUserRow(user, remove))}
    </Section>
  );
};

export default UsersAndGroupsManagement;
